// src/services/NotificationService.js

/**
 * Platform flags that work across Web, Electron, and Capacitor.
 */
const isElectron = !!(typeof window !== 'undefined' && (
  window.process?.versions?.electron || window.electron
));

const isCapacitor = !!(typeof window !== 'undefined' &&
  window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()
);

const isWeb = !isElectron && !isCapacitor;

let CapacitorLocalNotifications = null; // lazy-loaded only if needed

export { isElectron, isCapacitor, isWeb };

/**
 * @typedef {Object} ShowOptions
 * @property {string} [body]
 * @property {string} [icon] - Local asset path; avoid external hardcoded URLs.
 * @property {('high'|'default'|'low')} [priority]
 * @property {boolean} [silent]
 * @property {any} [data]
 * @property {Object[]} [actions] - Optional action buttons (web/pwa only)
 */

/**
 * @typedef {Object} ScheduleOptions
 * @property {number} id - Unique id per scheduled notification.
 * @property {Date|number|string} at - When to fire. Date object preferred.
 * @property {string} [body]
 * @property {string} [icon]
 * @property {any} [data]
 * @property {('min'|'low'|'default'|'high'|'max')} [androidPriority]
 * @property {string} [channelId] - Android channel id (Capacitor). Ensure channel exists.
 */

/**
 * NotificationService — unified local/device notifications.
 * - Electron: HTML5 Notification in renderer, or `window.electron.notify` if IPC bridge is present.
 * - Capacitor: uses `@capacitor/local-notifications`.
 * - Web/PWA: uses `Notification` API or Service Worker `showNotification` fallback.
 */
export const NotificationService = {
  /**
   * Initialize permissions and resources. Call once (e.g., in App.vue mounted).
   * @returns {Promise<void>}
   */
  async init() {
    if (isCapacitor) {
      try {
        const mod = await import('@capacitor/local-notifications');
        CapacitorLocalNotifications = mod.LocalNotifications;

        const perm = await CapacitorLocalNotifications.checkPermissions();
        if (perm.display !== 'granted') {
          await CapacitorLocalNotifications.requestPermissions();
        }

        // Create a default channel on Android (safe if it already exists)
        if (CapacitorLocalNotifications.createChannel) {
          try {
            await CapacitorLocalNotifications.createChannel({
              id: 'default_high',
              name: 'Default (High)',
              description: 'General notifications',
              importance: 4, // IMPORTANCE_HIGH for heads-up
              visibility: 1
            });
          } catch {/* no-op if exists */}
        }
      } catch (e) {
        // Capacitor LocalNotifications not available
      }
      return;
    }

    // Electron/Web — request browser notification permission if needed
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { await Notification.requestPermission(); } catch {}
    }
  },

  /**
   * Check current notification permission status across platforms.
   * @returns {Promise<'granted'|'denied'|'prompt'|'unknown'>}
   */
  async getPermissionStatus() {
    if (isCapacitor) {
      try {
        if (!CapacitorLocalNotifications) {
          const mod = await import('@capacitor/local-notifications');
          CapacitorLocalNotifications = mod.LocalNotifications;
        }
        const perm = await CapacitorLocalNotifications.checkPermissions();
        return perm.display || 'unknown';
      } catch {
        return 'unknown';
      }
    }
    if (typeof Notification !== 'undefined') {
      // browsers use 'granted'|'denied'|'default'
      return Notification.permission === 'default' ? 'prompt' : Notification.permission;
    }
    return 'unknown';
  },

  /**
   * Ensure permission is granted. Requests if needed.
   * @returns {Promise<'granted'|'denied'|'prompt'|'unknown'>}
   */
  async ensurePermission() {
    if (isCapacitor) {
      try {
        if (!CapacitorLocalNotifications) {
          const mod = await import('@capacitor/local-notifications');
          CapacitorLocalNotifications = mod.LocalNotifications;
        }
        let perm = await CapacitorLocalNotifications.checkPermissions();
        if (perm.display !== 'granted') {
          perm = await CapacitorLocalNotifications.requestPermissions();
        }
        return perm.display || 'unknown';
      } catch {
        return 'unknown';
      }
    }
    if (typeof Notification !== 'undefined') {
      if (Notification.permission === 'default') {
        try { await Notification.requestPermission(); } catch {}
      }
      return Notification.permission === 'default' ? 'prompt' : Notification.permission;
    }
    return 'unknown';
  },

  /**
   * Show an immediate notification.
   * @param {string} title
   * @param {ShowOptions} [options]
   * @returns {Promise<any>}
   */
  async show(title, options = {}) {
    if (!title) return;

    // Check if Android notifications are disabled
    if (isCapacitor && options.disableOnAndroid) {
      console.log('Android notifications are disabled');
      return;
    }

    if (isCapacitor && CapacitorLocalNotifications) {
      const id = Date.now() % 2147483647;
      await CapacitorLocalNotifications.schedule({
        notifications: [{
          id,
          title,
          body: options.body,
          largeIcon: options.icon,
          // Important on Android: a valid monochrome resource name (configured in android res)
          smallIcon: 'ic_stat_notify',
          extra: options.data,
          channelId: 'default_high',
          schedule: undefined,
          ongoing: false
        }]
      });
      return id;
    }

    if (isElectron) {
      // Prefer main-process via IPC if exposed
      if (window?.electron?.notify) {
        return window.electron.notify({ title, body: options.body, icon: options.icon, silent: options.silent });
      }
      // Fallback: renderer Notification
      if (typeof Notification !== 'undefined') {
        if (Notification.permission === 'granted') {
          return new Notification(title, { body: options.body, icon: options.icon, silent: options.silent });
        } else {
          try {
            const p = await Notification.requestPermission();
            if (p === 'granted') return new Notification(title, { body: options.body, icon: options.icon, silent: options.silent });
          } catch {}
        }
      }
      return;
    }

    // Web/PWA
    if (typeof Notification !== 'undefined') {
      if (Notification.permission === 'granted') {
        return new Notification(title, { body: options.body, icon: options.icon, data: options.data });
      } else if (Notification.permission === 'default') {
        try {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            return new Notification(title, { body: options.body, icon: options.icon, data: options.data });
          }
        } catch (e) {
          // Notification permission request failed
        }
      }
    }

    // Fallback: Service Worker (requires registration)
    if (typeof navigator !== 'undefined' && navigator.serviceWorker) {
      try {
        const reg = await navigator.serviceWorker.ready;
        await reg.showNotification(title, {
          body: options.body,
          icon: options.icon,
          data: options.data,
          actions: options.actions || [],
          requireInteraction: false
        });
      } catch (e) {
        // SW showNotification failed
      }
    } else {
      // Notification not supported
    }
  },

  /**
   * Schedule a notification in the future.
   * - Capacitor: uses native scheduling.
   * - Web/Electron: dev-time `setTimeout` while app is open (no background delivery).
   * @param {string} title
   * @param {ScheduleOptions} options
   */
  async schedule(title, options) {
    if (!title || !options || typeof options.id !== 'number') {
      throw new Error('schedule(title, { id, at, ... }) requires numeric id and at');
    }

    if (isCapacitor && CapacitorLocalNotifications) {
      const when = options.at instanceof Date
        ? options.at
        : typeof options.at === 'number'
          ? new Date(options.at)
          : new Date(options.at);

      await CapacitorLocalNotifications.schedule({
        notifications: [{
          id: options.id,
          title,
          body: options.body,
          largeIcon: options.icon,
          schedule: { at: when },
          extra: options.data,
          channelId: options.channelId || 'default_high',
          smallIcon: 'ic_stat_notify',
          ongoing: false
        }]
      });
      return;
    }

    // Lightweight fallback for web/electron while the app is open
    const whenMs = options.at instanceof Date ? options.at.getTime() : new Date(options.at).getTime();
    const delay = Math.max(0, whenMs - Date.now());
    setTimeout(() => this.show(title, { body: options.body, icon: options.icon, data: options.data }), delay);
  },

  /**
   * Cancel scheduled notifications (Capacitor only). No-op elsewhere.
   * @param {number|number[]} ids
   */
  async cancel(ids) {
    if (isCapacitor && CapacitorLocalNotifications) {
      const list = Array.isArray(ids) ? ids : [ids];
      await CapacitorLocalNotifications.cancel({ notifications: list.map(id => ({ id })) });
    }
  }
};
